import * as amp from '@amplitude/analytics-browser';
import { Experiment, ExperimentClient } from '@amplitude/experiment-js-client';
import { configuration } from '@Common/Helpers/configuration';

type UserProperties = {
    account_status: string;
    activated: boolean;
    authentication_status: string | null;
    from_CA: boolean;
    from_LCL: boolean;
    organisation_type: string | null;
    organization_active_member_count: string | null;
    organization_country: string;
    organization_employees: string | null;
    organization_id: string;
    organization_needs: string;
    organization_sector: string | null;
    organization_type: string;
    organization_user_count: string;
    plan_name: string;
    regional_bank: string;
    user_email: string;
    user_id: string;
    user_language: string;
};

declare global {
    interface GlobalEventHandlersEventMap {
        userPropertiesReady: CustomEvent<UserProperties>;
    }
}

export const setUserProperties = (userProps: UserProperties) => {
    const identity = new amp.Identify();

    for (const key in userProps) {
        if (key === 'organization_needs') {
            identity.set(key, JSON.parse(userProps[key as keyof UserProperties] as string) || {});
            continue;
        }
        const value = userProps[key as keyof UserProperties];
        // Preserve boolean false values, only convert null/undefined to empty string
        identity.set(key, value === null || value === undefined ? '' : value);
    }

    amp.identify(identity);
};

let amplitudeLoaded = false;

let callbacks: (() => void)[] = [];

export const callWhenReady = (callback: () => void) => {
    if (amplitudeLoaded) {
        callback();
    } else {
        callbacks.push(callback);
    }
};

const callBacklog = () => {
    amplitudeLoaded = true;
    callbacks.forEach(callback => callback());
    callbacks = [];
};

const storageKey = 'automation';
const versionKey = 'tracking_version';
const expirationKey = 'tracking_expiration';
const EXPIRATION_HOURS = 24;

const tryGetUserProperties = (): UserProperties | null => {
    const storedData = localStorage.getItem(storageKey);
    const storedVersion = localStorage.getItem(versionKey);
    const storedExpiration = localStorage.getItem(expirationKey);
    const currentVersion = configuration.appVersion;

    if (!storedData) {
        return null;
    }

    if (storedVersion !== currentVersion || !currentVersion) {
        return null;
    }

    if (storedExpiration) {
        const expirationTime = parseInt(storedExpiration, 10);
        const now = Date.now();
        const expirationMs = EXPIRATION_HOURS * 60 * 60 * 1000;

        if (now - expirationTime > expirationMs) {
            // Data has expired
            return null;
        }
    } else {
        // No expiration timestamp found, treat as invalid
        return null;
    }

    try {
        const userProperties = JSON.parse(storedData || '[{}]');
        return userProperties[0] || null;
    } catch {
        // Invalid JSON, clear corrupted data
        localStorage.removeItem(storageKey);
        localStorage.removeItem(versionKey);
        localStorage.removeItem(expirationKey);
        return null;
    }
};

export const loadUserProperties = async () => {
    if (!configuration.canTrackEvents) {
        // If canTrackEvents is false, just load experiments without setting user properties to avoid in-between rendering states
        await experiment?.fetch();
        callBacklog();
        return;
    }

    const userProperties = tryGetUserProperties();

    if (userProperties) {
        // Use cached data
        setUserProperties(userProperties);
        await experiment?.fetch();
        callBacklog();
    } else {
        // No valid cached data - wait for fresh data from backend
        document.addEventListener('userPropertiesReady', async (event: CustomEvent<UserProperties>) => {
            setUserProperties(event.detail);

            await experiment?.fetch();
            callBacklog();
        });
    }
};

export let experiment: ExperimentClient | null = null;
export const loadAmplitude = () => {
    if (import.meta.env.VITE_AMPLITUDE_API_KEY) {
        amp.init(import.meta.env.VITE_AMPLITUDE_API_KEY, configuration.userId, {
            appVersion: import.meta.env.VITE_APP_VERSION || '',
            autocapture: {
                sessions: false,
                elementInteractions: false,
                fileDownloads: false,
                pageViews: false,
                formInteractions: false,
                attribution: true
            }
        });

        experiment = Experiment.initializeWithAmplitudeAnalytics(import.meta.env.VITE_AMPLITUDE_API_KEY);

        loadUserProperties();
    }
};